import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth-store';
import { msUntilExpiry } from '../../../lib/jwt';
import { AUTH_EXPIRED_EVENT, dispatchAuthExpired, isAuthRedirectSuppressed } from '../../../lib/api';
import {
  TRANSIENT_RETRY_MS,
  adminTokenStore,
  canRefreshAdminSession,
  msUntilProactiveRefreshAllowed,
  proactiveRefreshDelay,
  refreshAdminSession,
} from '../../../lib/token-refresh';

interface SessionExpiryListenerProps {
  children: ReactNode;
}

/**
 * Two responsibilities, both bound to the React Router so we can navigate
 * within the SPA instead of triggering a full page reload:
 *
 * 1. Keep the session alive: shortly before the access token's `exp`
 *    (ATL-001144 — tokens live 15 minutes) renew it with the refresh token.
 *    Only when it cannot be renewed (no refresh token — e.g. impersonation —
 *    or the server refuses it) fire AUTH_EXPIRED_EVENT, so an idle tab still
 *    doesn't keep showing protected content past expiry.
 * 2. Listen for AUTH_EXPIRED_EVENT (from this timer or from the axios 401
 *    interceptor) and redirect to /login, preserving the current path so
 *    the user can return after re-auth.
 */
export function SessionExpiryListener({ children }: SessionExpiryListenerProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();
  const location = useLocation();

  // Order matters: register the AUTH_EXPIRED listener BEFORE scheduling the
  // TTL effect, so a token that is already expired at mount time still hits
  // a listener (the dispatch in the TTL effect is synchronous).
  useEffect(() => {
    const handler = () => {
      if (location.pathname === '/login') return;
      useAuthStore.getState().logout();
      navigate('/login', { replace: true });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, [navigate, location]);

  useEffect(() => {
    if (!accessToken) return;
    const ms = msUntilExpiry(accessToken);
    if (ms === null) return;

    // No refresh token (e.g. impersonation): the session ends at `exp`,
    // exactly as before.
    if (!canRefreshAdminSession()) {
      if (ms <= 0) {
        dispatchAuthExpired('token-ttl');
        return;
      }
      const id = window.setTimeout(() => dispatchAuthExpired('token-ttl'), ms);
      return () => window.clearTimeout(id);
    }

    // Renewable: refresh about a minute early. On success the store's
    // accessToken changes and this effect re-arms the timer for the new token.
    // A transient failure (offline — e.g. just woken from sleep —, 5xx, rate
    // limit) keeps the session and retries; only a session that can no longer
    // be renewed is ended.
    let cancelled = false;
    let id: number | undefined;
    const renew = () => {
      void refreshAdminSession(accessToken).then((outcome) => {
        if (cancelled || outcome.status === 'renewed') return;
        if (outcome.status === 'transient') {
          id = window.setTimeout(renew, TRANSIENT_RETRY_MS);
          return;
        }
        // During a demo reset the users are being recreated and the reset
        // re-logs in on its own — a failed renewal must not sign out.
        if (!isAuthRedirectSuppressed()) dispatchAuthExpired('token-ttl');
      });
    };
    id = window.setTimeout(
      renew,
      Math.max(proactiveRefreshDelay(ms), msUntilProactiveRefreshAllowed(adminTokenStore)),
    );
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [accessToken]);

  return <>{children}</>;
}
